import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, type Api } from './api.js';
import { Icon } from './shell.js';
import { Pill, toast } from './ui.js';
import { pageTour, playTour, TOUR_TEXT } from './tour.js';

/**
 * Klara, the Hub's helper (owner decisions 2026-10-15): the "?" button opens a small chat box (owner 2026-10-28) where
 * people ask how the Hub works and tap suggested questions. When a question is really a bug or a wish, Klara drafts a ticket; it is saved only with "Save ticket".
 * Staff see the language they picked (English by default); freelancers on their personal link get Swedish. Klara answers in
 * the language asked, and in the person's own language when that is unclear.
 */

export type TicketKind = 'bug' | 'idea' | 'question';
export const TICKET_KINDS: TicketKind[] = ['bug', 'idea', 'question'];
export type TicketStatus = 'new' | 'in_progress' | 'done' | 'declined';
export interface TicketSummary {
  id: string;
  number: number;
  kind: TicketKind;
  title: string;
  status: TicketStatus;
  votes: number;
  votedByMe: boolean;
}
export interface TicketView extends TicketSummary {
  description: string;
  page: string | null;
  question: string | null;
  mine: boolean;
  createdByName: string | null;
  triageNote: string | null;
  statusAt: string | null;
  createdAt: string;
}
interface Proposal {
  kind: TicketKind;
  title: string;
  description: string;
}
interface ChatReply {
  id?: string;
  reply: string;
  suggestions?: string[];
  proposal: Proposal | null;
  similar: TicketSummary[];
  remaining: number;
}
interface Msg {
  role: 'user' | 'klara';
  text: string;
  error?: boolean;
  /** Klara's own lines in the box (not from the model): not sent back as history */
  local?: boolean;
  /** the answer's id in the log, for "Did it help?" */
  id?: string;
  suggestions?: string[];
  helpful?: boolean;
  /** a button under a local line that asks this for the person */
  action?: { label: string; text: string };
  proposal?: Proposal & { question: string; saved?: number };
  similar?: TicketSummary[];
}

export type Lang = 'en' | 'sv';
const T = {
  en: {
    open: 'Ask Klara',
    sub: 'Help with the Hub',
    mine: 'My tickets',
    menu: 'More',
    back: 'Back to the chat',
    minimize: 'Minimize Klara',
    placeholder: 'Write a question…',
    send: 'Send',
    thinking: 'Klara is thinking…',
    hello: (name: string, page: string | null) => `Hi ${name}! What do you need help with${page ? ` on ${page}` : ''}?`,
    helped: 'Did it help?',
    yes: '👍 Yes',
    no: 'No',
    thanks: 'Thanks!',
    sorry: 'Sorry about that. Write what you were looking for, or let me turn it into a ticket for the admin.',
    makeTicket: 'Make it a ticket',
    ticketFrom: (q: string) => `The answer didn't help. Make a ticket of my question: ${q}`,
    toAdmin: 'Ticket for the admin',
    off: 'Klara is not connected yet. An admin connects her to Gemini.',
    save: 'Send',
    notNow: 'No thanks',
    saved: (n: number) => `Saved as ticket #${n}. You can follow it under My tickets.`,
    title: 'Title',
    description: 'Description',
    similar: 'Already filed – +1 one of these instead?',
    noTickets: "You haven't filed any tickets.",
    note: 'Note from the admin',
    remaining: (n: number) => `${n} questions left today`,
    clear: 'New conversation',
    report: "Something doesn't work",
    reportStart: "Something doesn't work: ",
    idea: 'I have an idea',
    ideaStart: 'I have an idea: ',
    isItOne: 'Already filed – +1 if it is the same thing:',
    kind: 'Kind',
    edit: 'Edit',
    done: 'Done',
    vote: (n: number, votes: number) => `+1 on ticket #${n} (${votes})`,
    loading: 'Loading…'
  },
  sv: {
    open: 'Fråga Klara',
    sub: 'Hjälp med Hubben',
    mine: 'Mina ärenden',
    menu: 'Mer',
    back: 'Tillbaka till chatten',
    minimize: 'Minimera Klara',
    placeholder: 'Skriv en fråga…',
    send: 'Skicka',
    thinking: 'Klara tänker…',
    hello: (name: string, page: string | null) => `Hej ${name}! Vad behöver du hjälp med${page ? ` på ${page}` : ''}?`,
    helped: 'Hjälpte det?',
    yes: '👍 Ja',
    no: 'Nej',
    thanks: 'Tack!',
    sorry: 'Tråkigt! Skriv vad du letade efter, eller låt mig göra ett ärende av det till admin.',
    makeTicket: 'Gör ett ärende',
    ticketFrom: (q: string) => `Svaret hjälpte inte. Gör ett ärende av min fråga: ${q}`,
    toAdmin: 'Ärende till admin',
    off: 'Klara är inte kopplad än. En admin kopplar henne till Gemini.',
    save: 'Skicka',
    notNow: 'Nej tack',
    saved: (n: number) => `Sparat som ärende #${n}. Du följer det under Mina ärenden.`,
    title: 'Rubrik',
    description: 'Beskrivning',
    similar: 'Redan inskickat – ge ett av dessa +1 i stället?',
    noTickets: 'Du har inte skickat in några ärenden.',
    note: 'Kommentar från admin',
    remaining: (n: number) => `${n} frågor kvar i dag`,
    clear: 'Ny konversation',
    report: 'Något fungerar inte',
    reportStart: 'Något fungerar inte: ',
    idea: 'Jag har en idé',
    ideaStart: 'Jag har en idé: ',
    isItOne: 'Redan anmält – ge +1 om det är samma sak:',
    kind: 'Typ',
    edit: 'Ändra',
    done: 'Klar',
    vote: (n: number, votes: number) => `+1 på ärende #${n} (${votes})`,
    loading: 'Laddar…'
  }
} as const;

/**
 * A hand-written ticket from its description alone: the first sentence becomes the title and the wording hints the kind
 * (a question mark, or "would be nice"-type phrasing for an idea, else a bug). The person can change both under More options.
 */
export function ticketDraft(description: string): { kind: TicketKind; title: string } {
  const text = description.trim().replace(/\s+/g, ' ');
  const first = (text.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? text).replace(/[.!]$/, '');
  const title = first.length > 80 ? `${first.slice(0, 79).replace(/\s+\S*$/, '')}…` : first;
  const kind: TicketKind = /(?<!\p{L})(idé|ide|förslag|önska[rs]?|vore (bra|kul|smidigt)|skulle vara|kan (ni|man|vi) (lägga|göra|få)|would be|could you|feature|suggest|wish)(?!\p{L})/iu.test(text)
    ? 'idea'
    : /\?\s*$/.test(first) || /^(hur|var|vad|varför|vem|när|kan jag|how|where|what|why|who|when|can i)(?!\p{L})/iu.test(text)
      ? 'question'
      : 'bug';
  return { kind, title };
}

export const KIND_LABEL: Record<Lang, Record<TicketKind, string>> = {
  en: { bug: 'Bug', idea: 'Idea', question: 'Question' },
  sv: { bug: 'Fel', idea: 'Idé', question: 'Fråga' }
};
export const STATUS_LABEL: Record<Lang, Record<TicketStatus, string>> = {
  en: { new: 'New', in_progress: 'In progress', done: 'Done', declined: 'Rejected' },
  sv: { new: 'Ny', in_progress: 'Pågår', done: 'Klar', declined: 'Nekad' }
};
export const STATUS_TONE: Record<TicketStatus, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = { new: 'info', in_progress: 'warn', done: 'ok', declined: 'neutral' };

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** What the person is looking at, by the first part of the path: the label in "Asking about" and starter questions. */
const PAGES: Record<string, { en: string; sv: string; ask: Record<Lang, string[]> }> = {
  '': { en: 'My work', sv: 'Mitt arbete', ask: { en: ['How do I request time off?', 'What does my week calendar show?', 'Where do I see the projects I work on?'], sv: ['Hur begär jag ledigt?', 'Vad visar min veckokalender?', 'Var ser jag projekten jag jobbar med?'] } },
  projects: { en: 'Projects', sv: 'Projekt', ask: { en: ['How do I filter projects by person?', 'Why is a project missing from the list?', 'What do the project statuses mean?'], sv: ['Hur filtrerar jag projekt på person?', 'Varför saknas ett projekt i listan?', 'Vad betyder projektens statusar?'] } },
  availability: { en: 'Availability', sv: 'Tillgänglighet', ask: { en: ['How do freelancers mark when they are available?', 'Who sees availability?'], sv: ['Hur anger frilansare när de är tillgängliga?', 'Vem ser tillgängligheten?'] } },
  team: { en: 'Team', sv: 'Team', ask: { en: ['What does over capacity mean?', 'Where do contracted hours come from?'], sv: ['Vad betyder över kapacitet?', 'Var kommer de avtalade timmarna ifrån?'] } },
  news: { en: 'News', sv: 'Nyheter', ask: { en: ['Who can post news?', 'How do I schedule a post?'], sv: ['Vem kan publicera nyheter?', 'Hur schemalägger jag en nyhet?'] } },
  people: { en: 'People', sv: 'Personer', ask: { en: ['How do I invite a freelancer?', 'How do I send someone a new personal link?'], sv: ['Hur bjuder jag in en frilansare?', 'Hur skickar jag en ny personlig länk till någon?'] } },
  agreements: { en: 'Quotes', sv: 'Offerter', ask: { en: ['How do I create a quote?', 'How does the price list work?'], sv: ['Hur skapar jag en offert?', 'Hur fungerar prislistan?'] } },
  tickets: { en: 'Tickets', sv: 'Ärenden', ask: { en: ['How do tickets work?', 'Who sees my tickets?'], sv: ['Hur fungerar ärenden?', 'Vem ser mina ärenden?'] } },
  settings: { en: 'Settings', sv: 'Inställningar', ask: { en: ['How do I turn a module on for a role?'], sv: ['Hur slår jag på en modul för en roll?'] } },
  t: { en: 'Your page', sv: 'Din sida', ask: { en: [], sv: ['Hur anger jag när jag är tillgänglig?', 'Vem ser min tillgänglighet?', 'Hur ändrar jag något jag redan skickat in?'] } }
};
const FALLBACK: Record<Lang, string[]> = { en: ['What can you help me with?', 'How do I request time off?'], sv: ['Vad kan du hjälpa mig med?'] };
export function pageInfo(path: string, lang: Lang): { label: string | null; ask: string[] } {
  const p = PAGES[path.split('/')[1] ?? ''];
  const ask = p?.ask[lang].length ? p.ask[lang] : FALLBACK[lang];
  return { label: p ? p[lang] : null, ask };
}

/** Opens Klara from anywhere (the command palette). */
export const openKlara = () => dispatchEvent(new CustomEvent('kff:klara'));

export function VoteButton(p: { api: Api; t: TicketSummary; lang?: Lang; onChange: (t: TicketSummary) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      class={`btn btn-sm${p.t.votedByMe ? ' btn-primary' : ''}`}
      aria-pressed={p.t.votedByMe}
      aria-label={T[p.lang ?? 'en'].vote(p.t.number, p.t.votes)}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          p.onChange(await p.api.post<TicketView>(`/api/tickets/${p.t.id}/vote`, { on: !p.t.votedByMe }));
        } catch (e) {
          toast({ text: msg(e) });
        } finally {
          setBusy(false);
        }
      }}
    >
      +1 · {p.t.votes}
    </button>
  );
}

/** **Bold** and `code` inline; the rest stays text (no HTML from the model). */
function inline(text: string) {
  return text.split(/(\*\*[^*\n]+\*\*|`[^`\n]+`)/).map((x) => (x.startsWith('**') && x.endsWith('**') && x.length > 4 ? <strong>{x.slice(2, -2)}</strong> : x.startsWith('`') && x.endsWith('`') && x.length > 2 ? x.slice(1, -1) : x));
}
/** Klara's answer as short paragraphs and lists: she may use **bold** for labels and '- ' or '1.' for steps (owner 2026-10-28). */
export function Rich(p: { text: string }) {
  const blocks: { list?: 'ul' | 'ol'; lines: string[] }[] = [];
  for (const raw of p.text.split('\n')) {
    const line = raw.trim();
    if (!line) {
      blocks.push({ lines: [] });
      continue;
    }
    const m = line.match(/^(?:([-*•])|(\d+)[.)])\s+(.*)$/);
    const list = m ? (m[1] ? 'ul' : 'ol') : undefined;
    const last = blocks[blocks.length - 1];
    if (last && last.list === list && (list || last.lines.length)) last.lines.push(m ? m[3]! : line);
    else blocks.push({ list, lines: [m ? m[3]! : line] });
  }
  return (
    <>
      {blocks
        .filter((b) => b.lines.length)
        .map((b) => {
          if (!b.list) return <p>{b.lines.map((l, i) => (i ? [<br />, inline(l)] : inline(l)))}</p>;
          const items = b.lines.map((l) => <li>{inline(l)}</li>);
          return b.list === 'ul' ? <ul>{items}</ul> : <ol>{items}</ol>;
        })}
    </>
  );
}

function ProposalCard(p: { api: Api; lang: Lang; m: Msg; page: string | null; onSaved: (n: number) => void; onDrop: () => void }) {
  const t = T[p.lang];
  const pr = p.m.proposal!;
  const [kind, setKind] = useState<TicketKind>(pr.kind);
  const [title, setTitle] = useState(pr.title);
  const [description, setDescription] = useState(pr.description);
  const [editing, setEditing] = useState(false);
  const [similar, setSimilar] = useState(p.m.similar ?? []);
  const [busy, setBusy] = useState(false);
  if (pr.saved) return null;
  return (
    <>
      {similar.length ? (
        <div class="kc-card" role="group" aria-label={t.isItOne}>
          <span class="small muted">{t.isItOne}</span>
          {similar.map((s) => (
            <div class="kc-similar">
              <span>
                <strong>#{s.number}</strong> {s.title}
              </span>
              <VoteButton api={p.api} t={s} lang={p.lang} onChange={(n) => setSimilar(similar.map((x) => (x.id === n.id ? n : x)))} />
            </div>
          ))}
        </div>
      ) : null}
      <div class="kc-card klara-card" role="group" aria-label={`${t.toAdmin}: ${title}`}>
        {editing ? (
          <>
            <div class="seg" role="radiogroup" aria-label={t.kind}>
              {TICKET_KINDS.map((k) => (
                <button type="button" role="radio" class="seg-tab" aria-checked={kind === k} aria-selected={kind === k} onClick={() => setKind(k)}>
                  {KIND_LABEL[p.lang][k]}
                </button>
              ))}
            </div>
            <label class="field">
              <span class="label">{t.title}</span>
              <input class="input" value={title} maxLength={120} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
            </label>
            <label class="field">
              <span class="label">{t.description}</span>
              <textarea class="textarea klara-desc" value={description} maxLength={4000} onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)} />
            </label>
          </>
        ) : (
          <>
            <span class={`kc-kind kc-kind--${kind}`}>{KIND_LABEL[p.lang][kind]}</span>
            <strong>{title}</strong>
            {description ? <span class="small muted kc-desc">{description}</span> : null}
          </>
        )}
        <div class="kc-chips">
          <button
            type="button"
            class="kc-chip kc-chip--pri"
            disabled={busy || title.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await p.api.post<TicketView>('/api/tickets', { kind, title, description, page: p.page ?? undefined, question: pr.question });
                p.onSaved(r.number);
              } catch (e) {
                toast({ text: msg(e) });
              } finally {
                setBusy(false);
              }
            }}
          >
            {t.save}
          </button>
          <button type="button" class="kc-chip" aria-pressed={editing} onClick={() => setEditing(!editing)}>
            {editing ? t.done : t.edit}
          </button>
          <button type="button" class="kc-chip kc-chip--ghost" onClick={p.onDrop}>
            {t.notNow}
          </button>
        </div>
      </div>
    </>
  );
}

function MyTickets(p: { api: Api; lang: Lang }) {
  const t = T[p.lang];
  const [rows, setRows] = useState<TicketView[] | null>(null);
  useEffect(() => {
    p.api.get<{ tickets: TicketView[] }>('/api/tickets?scope=mine').then(
      (r) => setRows(r.tickets),
      () => setRows([])
    );
  }, []);
  if (!rows) return <p class="small muted">{t.loading}</p>;
  if (!rows.length) return <p class="small muted">{t.noTickets}</p>;
  return (
    <ul class="list">
      {rows.map((r) => (
        <li class="kc-card">
          <span>
            #{r.number} {r.title} <Pill tone={STATUS_TONE[r.status]}>{STATUS_LABEL[p.lang][r.status]}</Pill>
          </span>
          {r.triageNote ? (
            <span class="small">
              <span class="muted">{t.note}:</span> {r.triageNote}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function KlaraPanel(p: { api: Api; lang: Lang; name?: string; onClose: () => void; msgs: Msg[]; setMsgs: (m: Msg[]) => void }) {
  const t = T[p.lang];
  const [view, setView] = useState<'ask' | 'mine'>('ask');
  const [menu, setMenu] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const self = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  // the page the person is on goes with every question; Klara uses it for context (owner 2026-10-28: no chip to manage)
  const page = typeof location === 'undefined' ? '/' : location.pathname;
  const info = pageInfo(page, p.lang);
  const tour = pageTour();
  const first = (p.name ?? '').split(/\s+/)[0] ?? '';
  useEffect(() => {
    p.api.get<{ configured: boolean }>('/api/klara').then(
      (r) => setConfigured(r.configured),
      () => setConfigured(false)
    );
    input.current?.focus();
    // a small box, not a dialog over the page: Escape minimizes it only while you are in it
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && self.current?.contains(document.activeElement)) p.onClose();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    box.current?.scrollTo?.({ top: box.current.scrollHeight });
  }, [p.msgs.length, busy, view]);

  const send = async (asked?: string) => {
    const text = (asked ?? q).trim();
    if (!text || busy) return;
    const next: Msg[] = [...p.msgs, { role: 'user', text }];
    p.setMsgs(next);
    setQ('');
    setBusy(true);
    try {
      const history = next.filter((m) => !m.error && !m.local).map((m) => ({ role: m.role, text: m.text }));
      const r = await p.api.post<ChatReply>('/api/klara/chat', { messages: history, page });
      p.setMsgs([...next, { role: 'klara', text: r.reply, id: r.id, suggestions: r.suggestions ?? [], ...(r.proposal ? { proposal: { ...r.proposal, question: text }, similar: r.similar } : {}) }]);
      setRemaining(r.remaining);
    } catch (e) {
      p.setMsgs([...next, { role: 'klara', text: e instanceof ApiError ? e.message : msg(e), error: true }]);
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };
  const update = (i: number, m: Msg) => p.setMsgs(p.msgs.map((x, j) => (j === i ? m : x)));
  const start = (text: string) => {
    setQ(text);
    input.current?.focus();
  };
  const rate = (i: number, helpful: boolean) => {
    const m = p.msgs[i]!;
    const question = [...p.msgs.slice(0, i)].reverse().find((x) => x.role === 'user')?.text ?? '';
    p.setMsgs([...p.msgs.slice(0, i), { ...m, helpful }, ...p.msgs.slice(i + 1), ...(helpful ? [] : [{ role: 'klara' as const, text: t.sorry, local: true, action: { label: t.makeTicket, text: t.ticketFrom(question) } }])]);
    if (m.id) void p.api.post('/api/klara/feedback', { id: m.id, helpful }).catch(() => undefined);
  };
  const last = p.msgs.length - 1;
  const off = configured === false;

  return (
    <section class="kc" role="dialog" aria-modal="false" aria-labelledby="klara-h" lang={p.lang} ref={self}>
      <header class="kc-head">
        <span class="kc-av" aria-hidden="true">
          K
        </span>
        <div class="kc-ti">
          <h2 id="klara-h">Klara</h2>
          <span>{t.sub}</span>
        </div>
        <div class="menu-wrap">
          <button type="button" class="kc-ic" aria-label={t.menu} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <Icon name="menu" />
          </button>
          {menu ? (
            <div class="menu kc-menu" role="menu">
              <button type="button" role="menuitem" class="menu-item" onClick={() => (setMenu(false), setView('mine'))}>
                <Icon name="ticket" />
                {t.mine}
              </button>
              <button type="button" role="menuitem" class="menu-item" disabled={!p.msgs.length} onClick={() => (setMenu(false), setView('ask'), p.setMsgs([]))}>
                <Icon name="plus" />
                {t.clear}
              </button>
            </div>
          ) : null}
        </div>
        <button type="button" class="kc-ic" aria-label={t.minimize} onClick={p.onClose}>
          <Icon name="chevron-down" />
        </button>
      </header>
      {view === 'mine' ? (
        <div class="kc-body">
          <button type="button" class="kc-chip kc-chip--ghost kc-back" onClick={() => setView('ask')}>
            ← {t.back}
          </button>
          <MyTickets api={p.api} lang={p.lang} />
        </div>
      ) : (
        <>
          <div class="kc-body klara-log" ref={box} aria-live="polite">
            {off ? <p class="callout callout--warn">{t.off}</p> : null}
            <div class="kc-b">
              <p>{t.hello(first, info.label)}</p>
            </div>
            {p.msgs.length ? null : (
              <div class="kc-chips klara-start">
                {/* the page's guided tour first, when it has one (tour.tsx) */}
                {tour ? (
                  <button type="button" class="kc-chip kc-chip--pri" onClick={() => (p.onClose(), playTour())}>
                    <Icon name="play" />
                    {TOUR_TEXT[p.lang].play}
                  </button>
                ) : null}
                {info.ask.map((a) => (
                  <button type="button" class="kc-chip" disabled={busy || off} onClick={() => void send(a)}>
                    {a}
                  </button>
                ))}
                <button type="button" class="kc-chip kc-chip--alt" onClick={() => start(t.reportStart)}>
                  <Icon name="alert" />
                  {t.report}
                </button>
                <button type="button" class="kc-chip kc-chip--alt" onClick={() => start(t.ideaStart)}>
                  <Icon name="bulb" />
                  {t.idea}
                </button>
              </div>
            )}
            {p.msgs.map((m, i) => (
              <>
                <div class={`kc-b${m.role === 'user' ? ' kc-me' : ''}${m.error ? ' klara-msg--err' : ''}`}>{m.role === 'klara' && !m.error ? <Rich text={m.text} /> : <p>{m.text}</p>}</div>
                {m.action && i === last ? (
                  <div class="kc-chips">
                    <button type="button" class="kc-chip" disabled={busy || off} onClick={() => void send(m.action!.text)}>
                      {m.action.label}
                    </button>
                  </div>
                ) : null}
                {m.proposal && !m.proposal.saved ? (
                  <ProposalCard
                    api={p.api}
                    lang={p.lang}
                    m={m}
                    page={page}
                    onDrop={() => update(i, { ...m, proposal: undefined })}
                    onSaved={(n) => {
                      p.setMsgs([...p.msgs.slice(0, i), { ...m, proposal: { ...m.proposal!, saved: n } }, ...p.msgs.slice(i + 1), { role: 'klara', text: t.saved(n), local: true }]);
                    }}
                  />
                ) : null}
                {i === last && m.role === 'klara' && !m.error && !m.local && !m.proposal ? (
                  <>
                    {m.suggestions?.length ? (
                      <div class="kc-chips">
                        {m.suggestions.map((s) => (
                          <button type="button" class="kc-chip" disabled={busy || off} onClick={() => void send(s)}>
                            {s}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {m.id ? (
                      <div class="kc-fb">
                        {m.helpful === undefined ? (
                          <>
                            <span>{t.helped}</span>
                            <button type="button" class="kc-chip kc-chip--sm" onClick={() => rate(i, true)}>
                              {t.yes}
                            </button>
                            <button type="button" class="kc-chip kc-chip--sm" onClick={() => rate(i, false)}>
                              {t.no}
                            </button>
                          </>
                        ) : m.helpful ? (
                          <span>{t.thanks}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            ))}
            {busy ? <p class="kc-b kc-busy muted">{t.thinking}</p> : null}
          </div>
          <form
            class="kc-in"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <label class="sr-only" for="klara-q">
              {t.placeholder}
            </label>
            <input id="klara-q" ref={input} class="kc-input" type="text" autocomplete="off" maxLength={2000} placeholder={t.placeholder} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
            <button type="submit" class="kc-send" aria-label={t.send} disabled={busy || !q.trim() || off}>
              <Icon name="arrow-right" />
            </button>
          </form>
          {remaining !== null && remaining <= 5 ? <p class="kc-left small muted">{t.remaining(remaining)}</p> : null}
        </>
      )}
    </section>
  );
}

/** The "Ask Klara" button, a small "?" bottom right; it opens the small chat box in its place, and the conversation stays
 * while you move between pages (owner 2026-10-28). */
export function KlaraLauncher(p: { api: Api; lang: Lang; name?: string }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const fab = useRef<HTMLButtonElement>(null);
  const was = useRef(false);
  useEffect(() => {
    const on = () => setOpen(true);
    addEventListener('kff:klara', on);
    return () => removeEventListener('kff:klara', on);
  }, []);
  // back on the "?" after minimizing, so the keyboard does not land at the top of the page
  useEffect(() => {
    if (was.current && !open) fab.current?.focus();
    was.current = open;
  }, [open]);
  return (
    <>
      {open ? null : (
        // a small round "?" so it covers as little of the page as possible (owner 2026-10-28)
        <button type="button" class="klara-fab" ref={fab} data-tour="help" title={T[p.lang].open} onClick={() => setOpen(true)}>
          <Icon name="help" />
          <span class="klara-fab-t">{T[p.lang].open}</span>
        </button>
      )}
      {open ? <KlaraPanel api={p.api} lang={p.lang} name={p.name} msgs={msgs} setMsgs={setMsgs} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
